import { useId } from 'react';

import { Checkbox } from '@/shared/components/ui/Checkbox/Checkbox';
import { Label } from '@/shared/components/ui/Label/Label';
import { RadioGroupItem } from '@/shared/components/ui/RadioGroup/RadioGroup';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  SELECTION_ROW_CLASSES,
  SELECTION_ROW_CONTROL_CLASSES,
  SELECTION_ROW_DESCRIPTION_CLASSES,
  SELECTION_ROW_LABEL_CLASSES,
} from '../selection.tokens';
import { SELECTION_ROW_ATTRIBUTE } from '../useSelectionKeyboard/useSelectionKeyboard';

import { type SelectionOptionRowProps } from './SelectionOptionRow.types';

/**
 * One option, in either cardinality.
 *
 * The control is a real `Checkbox` or `RadioGroupItem` rather than a listbox
 * row, and that is an accessibility decision rather than a stylistic one: `cmdk`
 * owns `aria-selected` for its roving highlight, so in a listbox the one
 * attribute meaning "this value is chosen" would instead mean "the cursor is
 * here". A checkbox says checked and a radio says checked, natively.
 *
 * `<Label htmlFor>` over the whole row rather than a click handler on a `<div>`:
 * a `<button>` is a labelable element, so the label activates the control for
 * free and the row is one target for a pointer, one announcement for a screen
 * reader, and one thing to keep in sync — instead of two.
 *
 * **Read-only is not disabled.** A disabled row is dimmed, announced as
 * unavailable and dropped from the tab order; a read-only row keeps its focus,
 * its contrast and its announcement, and simply refuses the write
 * (`aria-readonly`, plus a logic hook that ignores the toggle). Conflating them
 * tells an operator their own data is broken.
 */
export function SelectionOptionRow({
  option,
  mode,
  isSelected,
  isActive,
  isCreated,
  isDisabled,
  isReadOnly,
  describedById,
  onSelect,
  onKeyDown,
  renderOption,
}: SelectionOptionRowProps) {
  const rowId = useId();
  const Icon = option.icon;

  const content =
    renderOption === undefined ? (
      <>
        <span className="flex min-w-0 items-center gap-sm">
          {Icon === undefined ? null : <Icon aria-hidden className="h-4 w-4 shrink-0 opacity-70" />}
          <span className="truncate">{option.label}</span>
        </span>
        {option.description === undefined ? null : (
          <span className={SELECTION_ROW_DESCRIPTION_CLASSES}>{option.description}</span>
        )}
      </>
    ) : (
      renderOption(option, { isSelected, isDisabled, isActive, isCreated })
    );

  return (
    <div
      className={cn(SELECTION_ROW_CLASSES)}
      aria-disabled={isDisabled ? true : undefined}
      data-created={isCreated ? 'true' : undefined}
    >
      {mode === 'multi' ? (
        <Checkbox
          id={rowId}
          checked={isSelected}
          disabled={isDisabled}
          aria-readonly={isReadOnly ? true : undefined}
          aria-describedby={describedById}
          // The roving tab stop: a forty-option checkbox group is forty presses
          // of Tab without it (see `useSelectionKeyboard`).
          tabIndex={isActive ? 0 : -1}
          {...{ [SELECTION_ROW_ATTRIBUTE]: option.value }}
          // On the control, not on the row: a `<div>` with a key handler is a
          // jsx-a11y violation, and this button is where focus already is.
          onKeyDown={onKeyDown}
          // Read-only is NOT short-circuited here: the refusal lives in the
          // logic hook, which is also what announces it. Refusing in two places
          // meant the row swallowed the click and the operator heard nothing.
          onCheckedChange={() => {
            onSelect(option.value);
          }}
          className={SELECTION_ROW_CONTROL_CLASSES}
        />
      ) : (
        <RadioGroupItem
          id={rowId}
          value={option.value}
          disabled={isDisabled}
          // No `aria-readonly` here: ARIA supports it on `radiogroup`, not on
          // `radio`, so setting it on the row would announce nothing at all.
          // `SelectionPanel` puts it on the group instead.
          aria-describedby={describedById}
          // Radix owns the roving tabindex for a radio group; the attribute is
          // here only so the "walk in from the filter" step can find the row.
          {...{ [SELECTION_ROW_ATTRIBUTE]: option.value }}
          className={cn(SELECTION_ROW_CONTROL_CLASSES, 'shrink-0')}
        />
      )}
      <Label htmlFor={rowId} className={SELECTION_ROW_LABEL_CLASSES}>
        {content}
      </Label>
    </div>
  );
}
